import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Languages, Sparkles, User, LogOut } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useCustomer } from "../context/CustomerContext";
import { Button } from "./ui/button";

export default function Navbar() {
  const { t, lang, toggleLang, settings, L } = useApp();
  const { user: customer, startLogin, logout: custLogout } = useCustomer();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [loc.pathname]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/services", label: t.nav.services },
    { to: "/offers", label: t.nav.offers },
    { to: "/my-bookings", label: t.nav.myBookings },
    { to: "/contact", label: t.nav.contact },
  ];
  const brand = settings ? L(settings, "brand_name") : t.brand;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "cb-glass shadow-sm py-2" : "bg-transparent py-4"
      }`}
      data-testid="main-navbar"
    >
      <nav className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          {settings?.logo_url ? (
            <img src={settings.logo_url} alt={brand} className="h-9 w-auto object-contain" />
          ) : (
            <span className="grid place-items-center h-9 w-9 rounded-full cb-gradient text-primary-foreground">
              <Sparkles className="h-5 w-5" />
            </span>
          )}
          <span className="font-display text-xl sm:text-2xl font-bold tracking-tight text-foreground truncate max-w-[45vw] sm:max-w-none">{brand}</span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                loc.pathname === l.to
                  ? "bg-secondary text-secondary-foreground"
                  : "text-foreground/70 hover:text-foreground hover:bg-muted"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle-button"
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold text-foreground/80 hover:bg-muted transition-colors"
          >
            <Languages className="h-4 w-4" />
            {lang === "ar" ? "EN" : "ع"}
          </button>
          {customer ? (
            <div className="hidden sm:flex items-center gap-1.5 ps-1">
              <span className="grid place-items-center h-9 w-9 rounded-full bg-secondary text-secondary-foreground overflow-hidden" title={customer.name}>
                {customer.picture ? <img src={customer.picture} alt="" className="h-full w-full object-cover" /> : <User className="h-4 w-4" />}
              </span>
              <button onClick={custLogout} data-testid="customer-logout-button" className="p-2 rounded-full hover:bg-muted text-foreground/70" title={t.common.logout}>
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={startLogin}
              data-testid="customer-login-button"
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold text-foreground/80 hover:bg-muted transition-colors"
            >
              <User className="h-4 w-4" /> {t.common.login}
            </button>
          )}
          <Button
            onClick={() => nav("/book")}
            data-testid="nav-book-now-button"
            className="hidden sm:inline-flex rounded-full px-6 font-semibold"
          >
            {t.nav.book}
          </Button>
          <button
            className="lg:hidden grid place-items-center h-11 w-11 rounded-full hover:bg-muted"
            aria-label="Menu"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden mt-2 mx-4 rounded-2xl cb-glass p-4 animate-fade-up" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={`px-4 py-3.5 rounded-xl text-base font-medium ${loc.pathname === l.to ? "bg-secondary text-secondary-foreground" : "text-foreground/80 hover:bg-muted"}`}
              >
                {l.label}
              </Link>
            ))}
            {customer ? (
              <button onClick={custLogout} data-testid="mobile-customer-logout" className="sm:hidden flex items-center gap-2 px-4 py-3.5 rounded-xl text-base font-medium text-foreground/80 hover:bg-muted">
                <LogOut className="h-5 w-5" /> {t.common.logout}
              </button>
            ) : (
              <button onClick={startLogin} data-testid="mobile-customer-login" className="sm:hidden flex items-center gap-2 px-4 py-3.5 rounded-xl text-base font-medium text-foreground/80 hover:bg-muted">
                <User className="h-5 w-5" /> {t.common.login}
              </button>
            )}
            <Button onClick={() => nav("/book")} className="mt-2 rounded-full w-full h-12 text-base font-semibold" data-testid="mobile-menu-book-btn">
              {t.nav.book}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
